import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";
import { traceRouteEvent } from "@/lib/route-debug-trace";

/**
 * Token por rota atualizado no MESMO commit em que o React reconcilia
 * uma mudança de location. Tanto a animação de fade quanto o reset
 * de scroll devem reagir a este token — assim eles compartilham
 * exatamente o mesmo frame, mesmo em dispositivos lentos.
 *
 * Por que não usar `pathname` direto?
 *  - Algumas rotas dinâmicas (`/chat/:id`) mudam parâmetro sem
 *    necessariamente "trocar de tela" — o token nos dá controle.
 *  - O número incrementa monotonicamente, então o `<PageTransition>`
 *    pode usá-lo como React `key` para forçar re-mount limpo do
 *    container animado, mesmo entre rotas-irmãs.
 */
interface RouteTransitionValue {
  /** Chave estável da rota atual (pathname + search). */
  routeKey: string;
  /** Inteiro monotônico que muda a cada transição confirmada. */
  token: number;
}

const RouteTransitionContext = createContext<RouteTransitionValue>({
  routeKey: "",
  token: 0,
});

const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

export const RouteTransitionProvider = ({ children }: { children: ReactNode }) => {
  const { pathname, search } = useLocation();
  const nextKey = pathname + search;

  const [state, setState] = useState<RouteTransitionValue>({
    routeKey: nextKey,
    token: 0,
  });
  const tokenRef = useRef(0);

  // useLayoutEffect garante que o token é incrementado durante o
  // commit do React — antes do próximo paint. Consumidores que
  // também usarem layout effects reagirão no MESMO frame.
  useIsomorphicLayoutEffect(() => {
    if (state.routeKey === nextKey) return;
    tokenRef.current += 1;
    traceRouteEvent("RouteTransition:commit", nextKey, {
      from: state.routeKey,
      to: nextKey,
      token: tokenRef.current,
    });
    setState({ routeKey: nextKey, token: tokenRef.current });
  }, [nextKey, state.routeKey]);

  const value = useMemo(() => state, [state]);
  return (
    <RouteTransitionContext.Provider value={value}>
      {children}
    </RouteTransitionContext.Provider>
  );
};

export const useRouteTransition = () => useContext(RouteTransitionContext);
